import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [tokenState, setTokenState] = useState('checking'); // checking | valid | invalid | expired
  const [tokenEmail, setTokenEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      return;
    }
    const verify = async () => {
      try {
        const result = await api.verifyResetToken(token);
        if (result.valid) {
          setTokenState('valid');
          setTokenEmail(result.email || '');
        } else if (result.reason === 'expired') {
          setTokenState('expired');
        } else {
          setTokenState('invalid');
        }
      } catch (err) {
        setTokenState('invalid');
      }
    };
    verify();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #D1FAE5 0%, #FEF3C7 100%)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="p-8 rounded-3xl shadow-lg border-2 border-slate-200">
          <div className="text-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-24 h-24 mx-auto mb-3 object-contain"
            />
            <h1 className="text-3xl font-heading font-bold text-emerald-600 tracking-tight" data-testid="reset-title">
              Reset Password
            </h1>
          </div>

          {tokenState === 'checking' && (
            <div className="text-center py-8" data-testid="reset-checking">
              <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
              <p className="text-slate-600">Verifying your reset link...</p>
            </div>
          )}

          {(tokenState === 'invalid' || tokenState === 'expired') && (
            <div className="text-center py-4" data-testid={`reset-${tokenState}`}>
              <div className="bg-red-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-12 h-12 text-red-500" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl font-heading font-bold text-red-700 mb-2">
                {tokenState === 'expired' ? 'Link Expired' : 'Invalid Reset Link'}
              </h2>
              <p className="text-slate-600 mb-6">
                {tokenState === 'expired'
                  ? 'This reset link has expired. Reset links are valid for 1 hour.'
                  : "This reset link is invalid or has already been used."}
              </p>
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                data-testid="reset-request-new-button"
              >
                Request a New Link
              </Button>
            </div>
          )}

          {tokenState === 'valid' && !success && (
            <>
              <p className="text-center text-slate-600 mb-6" data-testid="reset-email-hint">
                Setting a new password for <span className="font-bold">{tokenEmail}</span>
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-3 flex items-start gap-2" data-testid="reset-error">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="password" className="text-slate-700 font-semibold">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 rounded-2xl border-2 focus:border-emerald-500 focus:ring-emerald-200"
                    placeholder="At least 6 characters"
                    required
                    data-testid="reset-password-input"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700 font-semibold">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 rounded-2xl border-2 focus:border-emerald-500 focus:ring-emerald-200"
                    placeholder="Re-enter your new password"
                    required
                    data-testid="reset-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                  data-testid="reset-submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}

          {success && (
            <div className="text-center py-4" data-testid="reset-success">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                className="bg-green-500 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4"
              >
                <Check className="w-12 h-12 text-white" strokeWidth={3} />
              </motion.div>
              <h2 className="text-2xl font-heading font-bold text-emerald-700 mb-2">Password Reset!</h2>
              <p className="text-slate-600 mb-6">
                Your password has been reset successfully. Redirecting you to login...
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                data-testid="reset-go-to-login"
              >
                Go to Login
              </Button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
